/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { TInspectorNode } from "../../inspector/InspectorNodeTypes";
import { useEffect, useState } from "react";
import { offEvent, onEvent } from "@adapptio/lib-shared";

const NODE_ID_PLACEHOLDER = "N";

export function useInspectorNode<NodeType extends TInspectorNode = TInspectorNode>(node: NodeType): void {
	const [ , setUpdateRef ] = useState(0);
	const uniqueDeps = node?.deps?.length > 0 ? Array.from(new Set(node.deps)) : [];
	const dependenciesId = uniqueDeps
		?.reduce((acc, dep) => (dep ? [ ...acc, dep.nodeId || NODE_ID_PLACEHOLDER ] : acc), [])
		.join(",");

	useEffect(() => {
		const unsubs: Array<() => void> = [];

		const handleModelChange = () => {
			setUpdateRef((x) => x + 1);
		};

		for (let i = 0; i < uniqueDeps.length; i++) {
			const depModel = uniqueDeps[i];

			if (depModel) {
				onEvent(depModel.changeEvent, handleModelChange);

				unsubs.push(() => offEvent(depModel.changeEvent, handleModelChange));
			}
		}

		handleModelChange();

		return () => {
			unsubs.forEach((unsubscribe) => unsubscribe());
		};
	}, [ dependenciesId ]);
}
